import { type PaginationState, TablePagination } from '@/components/shared/TablePagination';
import { Button } from '@/components/ui-kit/button';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { cn } from '@/lib/utils';
import { Loader2, RotateCcw, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import DocumentStatusBadge from '@/modules/org/contracts/components/document-status-badge';
import type { ContractDocumentRow } from '@/modules/org/contracts/constants/mock-contract-documents';

interface TrashDataCardViewProps {
  documents: ContractDocumentRow[];
  isLoading?: boolean;
  loadingRowCount?: number;
  emptyMessage?: string;
  /** Mirrors the desktop table's per-row loading flag — the parent passes
      the same `updatingItemId` it uses for the table so the Restore
      spinner stays in lockstep. */
  isUpdating?: boolean;
  updatingItemId?: string | null;
  onRestore: (row: ContractDocumentRow) => void;
  pagination?: PaginationState;
  onPageChange?: (page: number) => void;
  showingLabel?: string;
  className?: string;
}

const TrashDataCardView = ({
  documents,
  isLoading = false,
  loadingRowCount = 5,
  emptyMessage,
  isUpdating = false,
  updatingItemId,
  onRestore,
  pagination,
  onPageChange,
  showingLabel,
  className,
}: TrashDataCardViewProps) => {
  const { t } = useTranslation();
  const skeletonRows = Math.max(loadingRowCount, 1);

  return (
    <div
      className={cn(
        'overflow-hidden rounded-xl border border-border bg-background',
        className
      )}
    >
      <div className="flex flex-col gap-3 p-3">
        {isLoading ? (
          Array.from({ length: skeletonRows }).map((_, i) => (
            <div
              key={`trash-card-skeleton-${i}`}
              className="rounded-lg border border-border bg-background p-4 space-y-3"
            >
              <Skeleton className="h-4 w-3/4 rounded-full" />
              <Skeleton className="h-3 w-1/2 rounded-full" />
              <Skeleton className="h-3 w-2/3 rounded-full" />
            </div>
          ))
        ) : documents.length === 0 ? (
          <div className="py-10 text-center text-muted-foreground">
            {emptyMessage ?? t('ORGTRASH_EMPTY')}
          </div>
        ) : (
          documents.map((row) => {
            const restoreLoading = isUpdating && updatingItemId === row.id;
            return (
              <article
                key={row.id}
                className="rounded-lg border border-border bg-background p-4 space-y-4"
              >
                {/* Top row: icon tile + title/subtitle, status badge in the
                    top-right corner (mirrors archive / template-card). */}
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 shrink-0 rounded-lg bg-selise-blue/10 flex items-center justify-center">
                    <Trash2 className="h-5 w-5 text-selise-blue" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-semibold truncate text-[hsl(var(--fg))]">
                        {row.title}
                      </h3>
                      <div className="shrink-0">
                        <DocumentStatusBadge status={row.status} />
                      </div>
                    </div>
                    <p className="text-sm text-globe-grey truncate">
                      {row.recipientsSummary ?? '-'}
                    </p>
                  </div>
                </div>

                {/* Metadata grid — Created + Trashed timestamps surfaced
                    side-by-side. `lastEditedAt` doubles as "trashed at"
                    here because the backend bumps `LastUpdatedDate` when
                    a doc is moved to trash (no dedicated TrashedAt
                    column). */}
                <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                  <div className="min-w-0">
                    <dt className="text-globe-grey">
                      {t('ORGCONTRACTS_COLUMNS_CREATEDDATE')}
                    </dt>
                    <dd className="font-medium text-[hsl(var(--fg))] truncate">
                      {row.createdAt ?? '-'}
                    </dd>
                  </div>
                  <div className="min-w-0">
                    <dt className="text-globe-grey">
                      {t('ORGTRASH_COLUMNS_TRASHEDAT', 'Trashed')}
                    </dt>
                    <dd className="font-medium text-[hsl(var(--fg))] truncate">
                      {row.lastEditedAt ?? '-'}
                    </dd>
                  </div>
                </dl>

                {/* Divider extending to card edges, then a full-width
                    Restore button at the bottom. Trash only exposes one
                    action — Restore — so it gets the whole row. */}
                <div className="-mx-4 border-t border-border" />

                <Button
                  variant="outline"
                  size="sm"
                  className="w-full text-selise-blue-500 hover:text-selise-blue-500/90"
                  onClick={() => onRestore(row)}
                  disabled={restoreLoading}
                >
                  {restoreLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RotateCcw className="h-4 w-4" />
                  )}
                  {t('ORGTRASH_ACTION_RESTORE')}
                </Button>
              </article>
            );
          })
        )}
      </div>

      {!isLoading && pagination && onPageChange ? (
        <div className="border-t border-border bg-background px-3 py-2">
          <TablePagination
            pagination={pagination}
            onPageChange={onPageChange}
            showingLabel={showingLabel}
          />
        </div>
      ) : null}
    </div>
  );
};

export default TrashDataCardView;
